// Material step — used by machined parts (step 2 of 5).
//
// Picker-driven version: replaces the freeform v1 inputs with a real material
// library picker tied to /materials, vendor-aware, plus a price-analytics
// readout for the selected material. Still preserves the CSM toggle + every
// legacy materialCost.* field for back-compat — the picker just layers a
// `materialId` reference on top, and a `ballparkPrice` temporary cost on
// `materialCost.ballparkPrice` for parts that are mid-flight on a real
// vendor quote.
//
// Two-column responsive layout (stacks below sm):
//   left  — search + category-chip filter, scrollable result list, inline
//           "+ Add new material" sub-form (creates a Material record and
//           auto-selects it).
//   right — selected-material analytics: vendor card, last-paid card,
//           thickness-comparison card (adjacent thicknesses parsed from
//           `spec` field within the same category).
//   below — ballpark-price input + warning banner (NOT customer-quoteable
//           on its own; QuoteDetailPage's Mark Sent will gate later).
//
// CSM (customer-supplied material) toggle stays at the top. When CSM is on,
// the picker UI collapses to just the handling-fee field — KMF isn't buying.
//
// Schema notes (additive):
//   - `Part.materialId?: string` — FK to /materials (NEW)
//   - `MaterialCost.ballparkPrice?: number` + `.ballparkSet?: boolean` + `.ballparkNote?: string` (NEW)
//   These are strictly additive; legacy perPiece/perBar/perSheet/handling-fee
//   fields untouched. Pricing-engine integration for ballparkPrice is a
//   separate slice; for this PR we just persist + warn.

import { useEffect, useMemo, useState } from 'react';
import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type {
  Material,
  MaterialCategory,
  MaterialCost,
  MaterialPriceSnapshot,
  Vendor,
} from '@/shared/crmTypes';
import { MATERIAL_CATEGORY_LABELS, MATERIAL_CATEGORY_COLORS } from '@/shared/crmTypes';
import { createMaterial, getMaterial, listMaterials } from '@/shared/materials';
import { listVendors } from '@/shared/vendors';

// Local label maps duplicated from SettingsPage so this file stays decoupled
// from the Settings UI. If a future cleanup lifts these into crmTypes, drop
// these and import. Mirrors the same maps verbatim.
const PRICE_UNIT_LABELS: Record<MaterialPriceSnapshot['unit'], string> = {
  per_lb: '$/lb',
  per_piece: '$/pc',
  per_bar: '$/bar',
  per_sheet: '$/sheet',
  per_pack: '$/pack',
  per_ft: '$/ft',
};

// Forms used by the inline "+ Add new material" sub-form. Subset that covers
// the common KMF stock shapes; user can pick "other" if exotic.
type MiniFormForm = 'bar' | 'round' | 'plate' | 'sheet' | 'tube' | 'block' | 'other';
const MINI_FORM_OPTIONS: MiniFormForm[] = [
  'bar',
  'round',
  'plate',
  'sheet',
  'tube',
  'block',
  'other',
];

export function MaterialStep({ part, onChange }: StepProps) {
  const csm = part.customerSuppliedMaterial === true;

  // ────────────── data load ──────────────
  const [materials, setMaterials] = useState<Material[] | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Selected material — looked up by part.materialId. Held locally so we can
  // surface analytics without round-tripping listMaterials on every render.
  const [selected, setSelected] = useState<Material | null>(null);

  // Search + filter state — same 200ms debounce as the Settings Materials tab.
  const [searchRaw, setSearchRaw] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [filter, setFilter] = useState<'all' | MaterialCategory>('all');
  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(searchRaw), 200);
    return () => window.clearTimeout(t);
  }, [searchRaw]);

  // Inline "+ Add new material" sub-form open/closed.
  const [addOpen, setAddOpen] = useState(false);

  // Initial load — materials + vendors together so the result rows can render
  // vendor-name pills without a second fetch per row.
  async function load() {
    setLoadError(null);
    try {
      const [mList, vList] = await Promise.all([
        listMaterials({ activeOnly: true }),
        listVendors(),
      ]);
      setMaterials(mList);
      setVendors(vList);
    } catch (e) {
      setLoadError(`Failed to load materials: ${(e as Error).message}`);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  // Resolve the selected material whenever part.materialId changes. Prefer the
  // in-memory list (no extra fetch); fall back to getMaterial if the id isn't
  // in the current list (e.g. the user just added one and we haven't reloaded).
  useEffect(() => {
    const id = part.materialId;
    if (!id) {
      setSelected(null);
      return;
    }
    const fromList = materials?.find((m) => m.id === id);
    if (fromList) {
      setSelected(fromList);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const m = await getMaterial(id);
        if (!cancelled) setSelected(m);
      } catch {
        if (!cancelled) setSelected(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [part.materialId, materials]);

  // Vendor-by-id map for row + analytics rendering.
  const vendorById = useMemo(() => {
    const m = new Map<string, Vendor>();
    for (const v of vendors) m.set(v.id, v);
    return m;
  }, [vendors]);

  // Per-category counts for the filter chips. Built off the full list so
  // "Aluminum (12)" reflects everything, not just the search-narrowed view.
  const categoryCounts = useMemo(() => {
    const out: Record<string, number> = { all: materials?.length ?? 0 };
    if (materials) {
      for (const m of materials) {
        out[m.category] = (out[m.category] ?? 0) + 1;
      }
    }
    return out;
  }, [materials]);

  // Filtered + sorted result list. Same conventions as Settings: filter +
  // search → sort active-first then alpha.
  const results = useMemo(() => {
    if (!materials) return [];
    const needle = searchDebounced.trim().toLowerCase();
    const filtered = materials.filter((m) => {
      if (filter !== 'all' && m.category !== filter) return false;
      if (needle && !m.name.toLowerCase().includes(needle)) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      const aActive = a.active !== false ? 1 : 0;
      const bActive = b.active !== false ? 1 : 0;
      if (aActive !== bActive) return bActive - aActive;
      return a.name.localeCompare(b.name);
    });
  }, [materials, filter, searchDebounced]);

  // ────────────── persist helpers ──────────────

  /** Picks a material from the library: stores the id on the Part and seeds
   *  materialCost.vendorId from the material's primary vendor for downstream
   *  consumers (pricing, PDF). Doesn't touch legacy perPiece/perBar/perSheet. */
  function selectMaterial(m: Material) {
    const primaryVendorId = m.vendorIds?.[0];
    const nextCost: MaterialCost = {
      ...(part.materialCost ?? {}),
      // Backwards-compat: keep stashing the material name in vendorPartNum
      // so legacy display paths (PDF, ReviewStep) still render something.
      vendorPartNum: m.name,
      ...(primaryVendorId ? { vendorId: primaryVendorId } : {}),
    };
    onChange({ materialId: m.id, materialCost: nextCost });
  }

  function clearMaterial() {
    // Drop the FK + any ballpark price; user is starting over. Build the
    // next cost by copying every field EXCEPT the ballpark trio — avoids
    // the unused-binding TS error you'd get from a destructuring rest.
    const cost = part.materialCost ?? {};
    const next: MaterialCost = {};
    for (const key of Object.keys(cost) as (keyof MaterialCost)[]) {
      if (key === 'ballparkPrice' || key === 'ballparkSet' || key === 'ballparkNote') continue;
      (next as Record<string, unknown>)[key] = cost[key];
    }
    onChange({ materialId: undefined, materialCost: next });
  }

  function toggleCsm(checked: boolean) {
    // Default the handling fee to 0 on first flip-on so the input renders.
    // Leaving the value parked on flip-off lets the user toggle back without
    // losing their entry.
    if (checked && part.csmHandlingFeePerPiece === undefined) {
      onChange({ customerSuppliedMaterial: true, csmHandlingFeePerPiece: 0 });
    } else {
      onChange({ customerSuppliedMaterial: checked });
    }
  }

  function setBallpark(rawValue: string, rawNote: string | undefined) {
    const trimmed = rawValue.trim();
    const next: MaterialCost = { ...(part.materialCost ?? {}) };
    if (trimmed === '') {
      // Clearing the field — drop the ballpark trio entirely so the
      // ballparkSet flag doesn't linger and trip Mark Sent later.
      delete next.ballparkPrice;
      delete next.ballparkSet;
      delete next.ballparkNote;
    } else {
      const n = Number.parseFloat(trimmed);
      next.ballparkPrice = Number.isFinite(n) ? n : 0;
      next.ballparkSet = true;
      if (rawNote !== undefined) {
        const tn = rawNote.trim();
        if (tn === '') delete next.ballparkNote;
        else next.ballparkNote = tn;
      }
    }
    onChange({ materialCost: next });
  }

  // ────────────── render ──────────────

  return (
    <div className="space-y-3">
      {/* ────── CSM toggle (unchanged from v1; lives above the picker) ────── */}
      <div className={`border rounded p-3 ${csm ? 'border-orange bg-orange/5' : 'border-line-dark bg-bg'}`}>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={csm}
            onChange={(e) => toggleCsm(e.target.checked)}
            className="mt-0.5 accent-orange"
          />
          <span className="text-sm">
            <span className="font-semibold text-text">Customer is supplying material</span>
            <span className="block text-[11px] text-text-mute mt-0.5">
              KMF doesn't buy stock — we charge a flat handling fee per piece instead.
            </span>
          </span>
        </label>

        {csm && (
          <div className="mt-3 pl-6">
            <Field label="Handling fee per piece ($)">
              <div className="w-40">
                <NumberInput
                  value={part.csmHandlingFeePerPiece}
                  onChange={(v) => onChange({ csmHandlingFeePerPiece: v ?? 0 })}
                  step={1}
                  min={0}
                  unit="$"
                  placeholder="5.00"
                  numpadTitle="Handling fee per piece"
                />
              </div>
            </Field>
          </div>
        )}
      </div>

      {/* When CSM is on, the picker collapses — KMF isn't buying stock. */}
      {!csm && (
        <>
          {loadError && (
            <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm flex items-center justify-between gap-3">
              <span>⚠ {loadError}</span>
              <button
                type="button"
                onClick={load}
                className="text-xs text-orange font-semibold hover:underline shrink-0"
              >
                Retry
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* ──────────── LEFT: picker ──────────── */}
            <div className="space-y-3">
              <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide">
                Material library
              </h3>

              {/* Search + category chips */}
              <div className="space-y-2">
                <input
                  type="search"
                  placeholder="Search by name…"
                  value={searchRaw}
                  onChange={(e) => setSearchRaw(e.target.value)}
                  className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
                />
                <div className="flex flex-wrap gap-1">
                  <CategoryChip
                    label={`All${categoryCounts.all ? ` (${categoryCounts.all})` : ''}`}
                    active={filter === 'all'}
                    onClick={() => setFilter('all')}
                  />
                  {(Object.keys(MATERIAL_CATEGORY_LABELS) as MaterialCategory[]).map((cat) => {
                    const count = categoryCounts[cat] ?? 0;
                    if (count === 0) return null; // only chips with results
                    return (
                      <CategoryChip
                        key={cat}
                        label={`${MATERIAL_CATEGORY_LABELS[cat]} (${count})`}
                        active={filter === cat}
                        onClick={() => setFilter(cat)}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Result list — scroll cap keeps the step from blowing past
                  the right column's analytics height. */}
              {materials === null ? (
                <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse text-sm">
                  Loading materials…
                </div>
              ) : results.length === 0 ? (
                <div className="bg-paper border border-line rounded p-6 text-center text-sm text-text-mute">
                  {searchDebounced || filter !== 'all'
                    ? 'No materials match this filter.'
                    : 'No materials in the library yet.'}
                </div>
              ) : (
                <div className="border border-line rounded bg-paper max-h-[420px] overflow-y-auto divide-y divide-line">
                  {results.map((m) => (
                    <MaterialRow
                      key={m.id}
                      material={m}
                      vendorById={vendorById}
                      selected={part.materialId === m.id}
                      onClick={() => selectMaterial(m)}
                    />
                  ))}
                </div>
              )}

              {/* + Add new material — inline mini-form */}
              {addOpen ? (
                <AddMaterialForm
                  vendors={vendors}
                  onCancel={() => setAddOpen(false)}
                  onCreated={async (newId) => {
                    setAddOpen(false);
                    await load();
                    // Auto-select by setting the id immediately — the load
                    // effect will hydrate `selected` on the next pass.
                    onChange({ materialId: newId });
                  }}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className="w-full text-sm text-orange border border-dashed border-orange/40 rounded py-2 hover:bg-orange/5 font-semibold"
                >
                  + Add new material
                </button>
              )}
            </div>

            {/* ──────────── RIGHT: analytics ──────────── */}
            <div className="space-y-3">
              <h3 className="text-[11px] font-semibold text-text-mute uppercase tracking-wide">
                Selected · price analytics
              </h3>
              {selected ? (
                <SelectedMaterialAnalytics
                  selected={selected}
                  materials={materials ?? []}
                  vendorById={vendorById}
                  onClear={clearMaterial}
                />
              ) : (
                <div className="border border-dashed border-line-dark rounded p-6 text-center text-sm text-text-mute italic bg-bg/40">
                  Pick a material from the library on the left to see vendor +
                  price history here.
                </div>
              )}
            </div>
          </div>

          {/* ──────────── BELOW: ballpark price ──────────── */}
          <BallparkBlock cost={part.materialCost} onChange={setBallpark} />
        </>
      )}
    </div>
  );
}

// ============================================================================
// Picker row
// ============================================================================

interface MaterialRowProps {
  material: Material;
  vendorById: Map<string, Vendor>;
  selected: boolean;
  onClick: () => void;
}

function MaterialRow({ material, vendorById, selected, onClick }: MaterialRowProps) {
  const vendorNames = (material.vendorIds ?? [])
    .slice(0, 3)
    .map((vid) => vendorById.get(vid)?.name)
    .filter((n): n is string => !!n);
  const extra = (material.vendorIds?.length ?? 0) - vendorNames.length;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-2 transition-colors ${
        selected ? 'bg-orange/10' : 'hover:bg-bg'
      }`}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className={`font-semibold ${selected ? 'text-orange' : 'text-navy'}`}>
          {material.name}
        </span>
        <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${MATERIAL_CATEGORY_COLORS[material.category]}`}>
          {MATERIAL_CATEGORY_LABELS[material.category]}
        </span>
        {material.spec && (
          <span className="text-[11px] text-text-mute font-mono">{material.spec}</span>
        )}
      </div>
      {(vendorNames.length > 0 || extra > 0) && (
        <div className="flex flex-wrap gap-1 mt-1">
          {vendorNames.map((n, i) => (
            <span
              key={i}
              className="px-1.5 py-0.5 bg-bg border border-line rounded text-[11px] text-text"
            >
              {n}
            </span>
          ))}
          {extra > 0 && (
            <span className="text-[11px] text-text-mute italic">+{extra} more</span>
          )}
        </div>
      )}
    </button>
  );
}

// ============================================================================
// Right column — selected material analytics
// ============================================================================

interface SelectedMaterialAnalyticsProps {
  selected: Material;
  materials: Material[];
  vendorById: Map<string, Vendor>;
  onClear: () => void;
}

function SelectedMaterialAnalytics({
  selected,
  materials,
  vendorById,
  onClear,
}: SelectedMaterialAnalyticsProps) {
  // Vendor pills — primary is first in vendorIds[].
  const vendorList = (selected.vendorIds ?? [])
    .map((vid) => vendorById.get(vid))
    .filter((v): v is Vendor => !!v);
  const primary = vendorList[0];

  // Last-paid line — vendor name resolved from the map.
  const lp = selected.lastPaid;
  const lpVendorName = lp?.vendorId ? vendorById.get(lp.vendorId)?.name : undefined;
  const lpDate = lp?.date?.slice(0, 10);

  // Thickness comparison — find sibling materials in the same category with a
  // different parsed thickness from `spec`. Only relevant for sheet/plate.
  const compare = useMemo(() => buildThicknessComparison(selected, materials), [selected, materials]);

  return (
    <div className="space-y-3">
      {/* Header — name + category + clear button */}
      <div className="border border-line rounded p-3 bg-paper">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-base font-semibold text-navy">{selected.name}</span>
              <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${MATERIAL_CATEGORY_COLORS[selected.category]}`}>
                {MATERIAL_CATEGORY_LABELS[selected.category]}
              </span>
            </div>
            {selected.spec && (
              <div className="text-xs text-text-mute font-mono mt-0.5">{selected.spec}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-text-mute hover:text-orange whitespace-nowrap shrink-0"
            title="Unpick this material"
          >
            ✕ Clear
          </button>
        </div>
      </div>

      {/* Vendor card */}
      <div className="border border-line rounded p-3 bg-paper">
        <div className="text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1.5">
          Vendors
        </div>
        {vendorList.length === 0 ? (
          <div className="text-xs text-text-mute italic">
            No vendor linked yet. Add one in Settings → Materials.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {vendorList.map((v, i) => {
                const isPrimary = i === 0;
                return (
                  <span
                    key={v.id}
                    className={`px-2 py-0.5 rounded text-xs font-semibold border ${
                      isPrimary
                        ? 'bg-orange/10 text-orange border-orange/40'
                        : 'bg-bg text-text border-line'
                    }`}
                  >
                    {isPrimary && '★ '}
                    {v.name}
                  </span>
                );
              })}
            </div>
            {primary && (
              <div className="text-[11px] text-text-mute mt-1.5">
                we buy this from <span className="font-semibold text-text">{primary.name}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Last paid card */}
      <div className="border border-line rounded p-3 bg-paper">
        <div className="text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1.5">
          Last paid
        </div>
        {lp ? (
          <div className="text-sm">
            <span className="font-mono font-semibold">${lp.price.toFixed(2)}</span>
            <span className="text-text-mute"> {PRICE_UNIT_LABELS[lp.unit]}</span>
            {lpDate && (
              <span className="text-text-mute"> on <span className="font-mono">{lpDate}</span></span>
            )}
            {lpVendorName && (
              <span className="text-text-mute"> to <span className="font-semibold text-text">{lpVendorName}</span></span>
            )}
            {lp.reference && (
              <div className="text-[11px] text-text-mute mt-0.5">ref: <span className="font-mono">{lp.reference}</span></div>
            )}
          </div>
        ) : (
          <div className="text-xs text-text-mute italic">No price history yet.</div>
        )}
      </div>

      {/* Thickness comparison card — only meaningful for sheet/plate forms */}
      {(selected.form === 'sheet' || selected.form === 'plate') && (
        <div className="border border-line rounded p-3 bg-paper">
          <div className="text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1.5">
            Thickness comparison
          </div>
          {compare.thinner == null && compare.thicker == null ? (
            <div className="text-xs text-text-mute italic">
              Add adjacent thicknesses to Materials to see comparisons here.
            </div>
          ) : (
            <div className="space-y-1.5 text-sm">
              {compare.thinner && (
                <ComparisonLine label="Thinner" entry={compare.thinner} vendorById={vendorById} />
              )}
              {compare.current && (
                <div className="flex items-baseline gap-2 text-text">
                  <span className="text-[10px] font-semibold text-orange uppercase tracking-wide w-16 shrink-0">
                    Current
                  </span>
                  <span className="font-mono font-semibold">{compare.current.thicknessLabel}</span>
                  <span className="text-text-mute text-xs">— {compare.current.name}</span>
                </div>
              )}
              {compare.thicker && (
                <ComparisonLine label="Thicker" entry={compare.thicker} vendorById={vendorById} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface ComparisonEntry {
  name: string;
  thickness: number;
  thicknessLabel: string;
  lastPaid?: MaterialPriceSnapshot;
}

interface ComparisonLineProps {
  label: 'Thinner' | 'Thicker';
  entry: ComparisonEntry;
  vendorById: Map<string, Vendor>;
}

function ComparisonLine({ label, entry, vendorById }: ComparisonLineProps) {
  const lp = entry.lastPaid;
  const vendorName = lp?.vendorId ? vendorById.get(lp.vendorId)?.name : undefined;
  return (
    <div className="flex items-baseline gap-2 text-text flex-wrap">
      <span className="text-[10px] font-semibold text-text-mute uppercase tracking-wide w-16 shrink-0">
        {label}
      </span>
      <span className="font-mono">{entry.thicknessLabel}</span>
      <span className="text-text-mute text-xs">— {entry.name}</span>
      {lp ? (
        <span className="text-xs text-text-mute">
          · last paid{' '}
          <span className="font-mono text-text font-semibold">${lp.price.toFixed(2)}</span>
          <span className="text-text-mute"> {PRICE_UNIT_LABELS[lp.unit]}</span>
          {vendorName && <span className="text-text-mute"> to {vendorName}</span>}
        </span>
      ) : (
        <span className="text-xs text-text-mute italic">· no price on file</span>
      )}
    </div>
  );
}

// ============================================================================
// Thickness parsing + comparison logic
// ============================================================================

/**
 * Pull a numeric thickness (in inches) out of a material's `spec` field.
 * Handles:
 *   - fractions: "1/4 plate", "3/16 sheet"
 *   - decimals : "0.25 thick", ".125 sheet"
 *   - mixed    : "1/2 G-10 sheet", "G-10 1/4"
 * Returns { value, label } where label is the human-readable thickness token
 * (e.g. "1/4", "0.125"), or null if nothing parseable is found.
 */
export function parseSpecThickness(
  spec: string | undefined,
): { value: number; label: string } | null {
  if (!spec) return null;
  const s = spec.trim();
  // Try fraction first — must be at a word boundary so "1/4 G-10" matches but
  // "1/4-inch-foo" still does. Anchored by start-of-string OR whitespace.
  const fracMatch = s.match(/(?:^|\s)(\d+)\s*\/\s*(\d+)/);
  if (fracMatch) {
    const num = Number(fracMatch[1]);
    const den = Number(fracMatch[2]);
    if (den > 0 && Number.isFinite(num) && Number.isFinite(den)) {
      return { value: num / den, label: `${num}/${den}` };
    }
  }
  // Decimal — accept ".125" or "0.125" or "0.5". Avoid matching things like
  // "G-10" (no digit-period-digit) or "2-1/4" (the fraction match would've
  // already won).
  const decMatch = s.match(/(?:^|\s)(\d*\.\d+)/);
  if (decMatch) {
    const v = Number(decMatch[1]);
    if (Number.isFinite(v) && v > 0) {
      return { value: v, label: decMatch[1] };
    }
  }
  return null;
}

interface ThicknessCompareResult {
  current?: ComparisonEntry;
  thinner?: ComparisonEntry;
  thicker?: ComparisonEntry;
}

/**
 * For the selected material, find the closest thinner and thicker siblings in
 * the same category. "Closest" = nearest parsed thickness on either side. If
 * nothing parseable on the selected material's spec, returns empty (the UI
 * surfaces the "add adjacent thicknesses" hint).
 */
function buildThicknessComparison(
  selected: Material,
  all: Material[],
): ThicknessCompareResult {
  const selT = parseSpecThickness(selected.spec);
  if (!selT) return {};
  // Sibling pool: same category, different id, sheet/plate form, with a
  // parseable spec thickness. We don't require an exact name match — Justin's
  // library uses one record per thickness (e.g. "G-10 phenolic 1/8" vs
  // "G-10 phenolic 1/4"), so category + form is the right join.
  const siblings = all
    .filter(
      (m) =>
        m.id !== selected.id &&
        m.category === selected.category &&
        (m.form === 'sheet' || m.form === 'plate') &&
        m.active !== false,
    )
    .map((m) => {
      const t = parseSpecThickness(m.spec);
      if (!t) return null;
      const entry: ComparisonEntry = {
        name: m.name,
        thickness: t.value,
        thicknessLabel: t.label,
        lastPaid: m.lastPaid,
      };
      return entry;
    })
    .filter((e): e is ComparisonEntry => e !== null);

  const thinnerCandidates = siblings
    .filter((s) => s.thickness < selT.value)
    .sort((a, b) => b.thickness - a.thickness); // closest-thinner first
  const thickerCandidates = siblings
    .filter((s) => s.thickness > selT.value)
    .sort((a, b) => a.thickness - b.thickness); // closest-thicker first

  return {
    current: {
      name: selected.name,
      thickness: selT.value,
      thicknessLabel: selT.label,
      lastPaid: selected.lastPaid,
    },
    thinner: thinnerCandidates[0],
    thicker: thickerCandidates[0],
  };
}

// ============================================================================
// Ballpark-price block — temporary placeholder cost
// ============================================================================

interface BallparkBlockProps {
  cost: MaterialCost | undefined;
  onChange: (rawValue: string, rawNote: string | undefined) => void;
}

function BallparkBlock({ cost, onChange }: BallparkBlockProps) {
  const value = cost?.ballparkPrice;
  const isSet = cost?.ballparkSet === true;
  const [noteOpen, setNoteOpen] = useState<boolean>(!!cost?.ballparkNote);
  const noteValue = cost?.ballparkNote ?? '';

  return (
    <div
      className={`border-2 rounded p-3 ${
        isSet ? 'border-orange/40 bg-orange/5' : 'border-dashed border-line-dark bg-bg/30'
      }`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <Field label="Ballpark price ($ / unit)">
          <div className="w-40">
            <NumberInput
              value={value}
              onChange={(v) => onChange(v === undefined ? '' : String(v), noteValue)}
              step={1}
              min={0}
              unit="$"
              placeholder="2.45"
              numpadTitle="Ballpark price"
            />
          </div>
        </Field>
        <div className="flex-1 min-w-[12rem]">
          <div className="text-[11px] text-orange font-semibold uppercase tracking-wide">
            ⚠ Temporary — not customer-quoteable
          </div>
          <div className="text-xs text-text-mute mt-0.5">
            Use until actual vendor quote comes back. Quotes can't be marked
            Sent while ballpark prices are present without confirmation.
          </div>
        </div>
      </div>

      {/* Optional note — visible once anything is set, or when user expands. */}
      {(isSet || noteOpen) && (
        <div className="mt-2">
          {!noteOpen && !noteValue ? (
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="text-[11px] text-orange hover:underline font-semibold"
            >
              + Add note
            </button>
          ) : (
            <Field label="Note (optional)">
              <input
                type="text"
                value={noteValue}
                onChange={(e) =>
                  // Re-passing the current price keeps it stable while the
                  // user edits the note. The note-input is only shown after
                  // `isSet` is true so `value` is guaranteed defined here,
                  // but we coerce defensively.
                  onChange(value !== undefined ? String(value) : '0', e.target.value)
                }
                placeholder='e.g. "based on last 1/4 G-10 job, waiting on Alro requote"'
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
              />
            </Field>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// "+ Add new material" inline mini-form
// ============================================================================

interface AddMaterialFormProps {
  vendors: Vendor[];
  onCancel: () => void;
  onCreated: (newId: string) => Promise<void> | void;
}

function AddMaterialForm({ vendors, onCancel, onCreated }: AddMaterialFormProps) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<MaterialCategory>('aluminum');
  const [form, setForm] = useState<MiniFormForm>('plate');
  const [densityRaw, setDensityRaw] = useState('');
  const [vendorIds, setVendorIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vendorOptions = useMemo(() => {
    return [...vendors].sort((a, b) => {
      if ((a.favorite ? 1 : 0) !== (b.favorite ? 1 : 0)) {
        return (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0);
      }
      return a.name.localeCompare(b.name);
    });
  }, [vendors]);

  function toggleVendor(vid: string) {
    setVendorIds((prev) =>
      prev.includes(vid) ? prev.filter((x) => x !== vid) : [...prev, vid],
    );
  }

  async function handleCreate() {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Material name is required.');
      return;
    }
    let density: number | undefined;
    if (densityRaw.trim() !== '') {
      const n = Number.parseFloat(densityRaw);
      if (!Number.isFinite(n) || n < 0) {
        setError('Density must be a number ≥ 0.');
        return;
      }
      density = n;
    }
    setBusy(true);
    try {
      const id = await createMaterial({
        name: trimmed,
        category,
        form,
        vendorIds,
        density,
        active: true,
      });
      await onCreated(id);
    } catch (e) {
      setError(`Failed to create: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  return (
    <div className="border-2 border-orange/40 rounded p-3 bg-orange/5 space-y-2.5">
      <div className="text-xs font-semibold text-navy uppercase tracking-wide">
        Add new material
      </div>

      <Field label="Name *">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. 6061-T6 Aluminum 1/4 plate"
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
        />
      </Field>

      <div className="grid grid-cols-2 gap-2.5">
        <Field label="Category">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as MaterialCategory)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
          >
            {(Object.keys(MATERIAL_CATEGORY_LABELS) as MaterialCategory[]).map((c) => (
              <option key={c} value={c}>
                {MATERIAL_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Form">
          <select
            value={form}
            onChange={(e) => setForm(e.target.value as MiniFormForm)}
            className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
          >
            {MINI_FORM_OPTIONS.map((f) => (
              <option key={f} value={f}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Density (lb/in³, optional)">
        <div className="w-40">
          <NumberInput
            value={densityRaw === '' ? undefined : Number.parseFloat(densityRaw)}
            onChange={(v) => setDensityRaw(v === undefined ? '' : String(v))}
            step={0.0625}
            min={0}
            placeholder="0.098"
            numpadTitle="Density (lb/in³)"
          />
        </div>
      </Field>

      {vendorOptions.length > 0 && (
        <div>
          <div className="text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
            Vendors (optional)
          </div>
          <div className="flex flex-wrap gap-1">
            {vendorOptions.map((v) => {
              const on = vendorIds.includes(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => toggleVendor(v.id)}
                  className={`px-2 py-0.5 rounded text-xs font-semibold border transition-colors ${
                    on
                      ? 'bg-orange text-white border-orange'
                      : 'bg-paper text-text border-line-dark hover:border-orange'
                  }`}
                >
                  {v.favorite ? '★ ' : ''}
                  {v.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <div className="text-xs text-orange">⚠ {error}</div>}

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1.5 text-sm rounded border border-line-dark text-text-mute hover:bg-bg disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={busy}
          className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create + select'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Tiny shared widgets
// ============================================================================

interface CategoryChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function CategoryChip({ label, active, onClick }: CategoryChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? 'bg-orange text-white border-orange'
          : 'bg-paper text-text border-line-dark hover:border-orange'
      }`}
    >
      {label}
    </button>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

function Field({ label, children, className }: FieldProps) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
